import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Table,
  TableColumnsType
} from 'antd'
import { useTheme } from 'antd-style'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  usePatientTreatmentServiceGetPatientTreatmentPlanLegalEntityListEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanListEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  TreatmentPlanSortBy2,
  TreatmentPlanStatus
} from 'src/api/requests'
import {
  PageBreadcrumbs,
  Select,
  Text,
  ClassifierInfiniteSelect
} from 'src/components'
import {
  TreatmentStatusTag,
  useTreatmentConfig
} from 'src/features/persons/components'
import {
  useClassifierId,
  useDefaultSortingItem,
  useTablePagination
} from 'src/hooks'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const Treatments = () => {
  const { t, currentLang } = useLocales()
  const pagination = useTablePagination()
  const token = useTheme()

  const navigate = useNavigate()
  const { options } = useTreatmentConfig()

  const classifierTreatmentType = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const { defaultSortingOption } = useDefaultSortingItem()
  const [treatmentType, setTreatmentType] = useState('')
  const [legalEntityId, setLegalEntityId] = useState('')
  const [date, setDate] = useState<[string, string]>(['', ''])
  const [ordering, setOrdering] = useState('')
  const [status, setStatus] = useState<TreatmentPlanStatus>()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as TreatmentPlanSortBy2,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(legalEntityId && { legalEntityId }),
    ...(treatmentType && { treatmentType }),
    ...(date?.[0] && date?.[1] && { startDate: date?.[0], endDate: date?.[1] }),
    ...(status && { status })
  }

  const { data, isLoading } =
    usePatientTreatmentServiceGetPatientTreatmentPlanListEndpoint(queryParams)

  const { data: institutions } =
    usePatientTreatmentServiceGetPatientTreatmentPlanLegalEntityListEndpoint()

  const institutionOptions = [
    defaultSortingOption,
    ...(institutions?.map((item) => ({ value: item.id, label: item.name })) ??
      [])
  ]

  const onFilterChange = <T,>(
    val: T,
    setState: Dispatch<SetStateAction<T>>
  ) => {
    setState(val)
    resetPagination(pagination)
  }

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'id',
        title: t('treatments.id'),
        render: (_, __, index) => `${index + 1}`
      },
      {
        key: 'type',
        width: 270,
        title: t('treatments.treatment_type'),
        dataIndex: ['translation', currentLang?.value]
      },
      {
        key: 'startDate',
        title: t('treatments.start'),
        render: ({ startDate }) => formatDate(startDate)
      },
      {
        key: 'endDate',
        title: t('treatments.end'),
        render: ({ endDate }) => formatDate(endDate)
      },
      {
        key: 'institution',
        title: t('treatments.institution'),
        dataIndex: ['legalEntity', 'name']
      },
      {
        key: 'status',
        title: t('treatments.status'),
        render: ({ status }) => {
          return <TreatmentStatusTag status={status} />
        }
      },

      {
        key: 'actions',
        render: ({ id }) => (
          <Button
            type="text"
            onClick={() => navigate(paths.treatments.details(id))}
          >
            <Text weight={400} color={token.colorPrimary}>
              {t('common.see_details')}
            </Text>
          </Button>
        ),
        width: 120
      }
    ],
    [t]
  )

  const onTypeChange = (val: { label: string; value: string }) => {
    if (val?.value === '') {
      setTreatmentType('')
    } else {
      onFilterChange(val?.label, setTreatmentType)
    }
  }

  const statusOptions = [
    defaultSortingOption,
    ...Object.values(TreatmentPlanStatus).map((item) => ({
      label: t(`treatments.${item}`),
      value: item
    }))
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.treatments')}
      items={[
        {
          title: t('menu.treatments')
        }
      ]}
    >
      <Flex gap={16}>
        <ClassifierInfiniteSelect
          labelInValue
          hasDefault
          onSelect={(val) => onTypeChange(val)}
          onClear={() => setTreatmentType('')}
          id={classifierTreatmentType}
          placeholder={t('treatments.treatment_type')}
          className="full-width"
          style={{
            minWidth: 400
          }}
        />

        <Select
          onSelect={(val?: TreatmentPlanStatus) =>
            onFilterChange(val, setStatus)
          }
          options={statusOptions}
          placeholder={t('form.status')}
        />

        <Select
          placeholder={t('common.institution')}
          options={institutionOptions}
          onSelect={(val: string) => onFilterChange(val, setLegalEntityId)}
        />

        <DatePicker.RangePicker
          className="full-width"
          format={[dayMonthYearBackSlash, dayMonthYearBackSlash]}
          onChange={(_, datesString) => onFilterChange(datesString, setDate)}
          placeholder={[
            t('notifications.from_date'),
            t('notifications.to_date')
          ]}
        />

        <Select
          onSelect={(val: string) => onFilterChange(val, setOrdering)}
          options={options}
        />
      </Flex>
      <Divider />
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
